import { supabase } from './supabase';
import { optimizeImageForUpload, PHOTO_PRESET } from './imageOptimize';

/**
 * Admin API layer — all calls go directly to Supabase via the user's session.
 * Every privileged operation is gated by RLS policies using the is_admin()
 * SECURITY DEFINER helper function.
 */



// ─── Audit Logging Helper ────────────────────────────────────────────────────

export async function logAction(
  action: string,
  targetTable: string,
  targetId: string | null,
  payload: Record<string, unknown> = {}
) {
  try {
    await supabase.rpc('log_admin_action', {
      p_action: action,
      p_target_table: targetTable,
      p_target_id: targetId,
      p_payload: payload
    });
  } catch {
    // Audit logging is best-effort; don't block the UI
    console.warn('[AdminAPI] Audit log failed for:', action);
  }
}

// ─── Overview ────────────────────────────────────────────────────────────────

export async function getOverview() {
  const nowIso = new Date().toISOString();
  const liveCampaigns = () =>
    supabase
      .from('ad_campaigns')
      .select('id', { count: 'exact', head: true })
      .eq('active', true)
      .not('is_paused', 'is', true)
      .eq('preview_only', false)
      .lte('starts_at', nowIso)
      .or(`ends_at.is.null,ends_at.gt.${nowIso}`);
  const staffLiveCount = async (festival: boolean) => {
    const res = await staffCampaigns({ live: true, festival, limit: 0 });
    return { count: res.total, error: null as { code?: string } | null };
  };
  const [driversRes, packagesRes, conciergeRes, listingStatsRes, sponsorsRes, festivalsRes, impressionsRes] = await Promise.allSettled([
    supabase.rpc('list_drivers_admin'),
    supabase.rpc('list_packages_admin', { p_days: 7, p_status: null, p_driver_id: null }),
    supabase.rpc('list_concierge_requests_admin', { p_status: 'pending' }),
    supabase.rpc('get_listing_event_stats', { p_days: 30 }),
    campaignsViaStaffReader ? staffLiveCount(false) : liveCampaigns().neq('placement', 'festival_event_takeover'),
    campaignsViaStaffReader ? staffLiveCount(true) : liveCampaigns().eq('placement', 'festival_event_takeover'),
    supabase.rpc('get_admin_impression_summary', { p_days: 30 }),
  ]);

  // Check for real RLS recursion (42P17) on the ad_campaigns probe
  let rlsRecursionDetected = false;
  if (sponsorsRes.status === 'fulfilled' && sponsorsRes.value.error?.code === '42P17') {
    rlsRecursionDetected = true;
  }
  if (festivalsRes.status === 'fulfilled' && festivalsRes.value.error?.code === '42P17') {
    rlsRecursionDetected = true;
  }

  // Online drivers count from list_drivers_admin
  let onlineDrivers = 0;
  let totalDrivers = 0;
  if (driversRes.status === 'fulfilled' && driversRes.value.data) {
    const d = driversRes.value.data as { ok?: boolean; drivers?: any[] };
    const drivers = d?.drivers ?? (Array.isArray(driversRes.value.data) ? driversRes.value.data : []);
    totalDrivers = drivers.length;
    onlineDrivers = drivers.filter((dr: any) => dr.is_online).length;
  }

  // Package stats from list_packages_admin
  let packagesLast7d = 0;
  let pendingPackages = 0;
  if (packagesRes.status === 'fulfilled' && packagesRes.value.data) {
    const p = packagesRes.value.data as { ok?: boolean; totals?: any; entries?: any[] };
    if (p?.totals) {
      packagesLast7d = (p.totals.delivered || 0) + (p.totals.pending || 0) + (p.totals.cancelled || 0);
      pendingPackages = p.totals.pending || 0;
    }
  }

  // Pending concierge count
  let pendingConcierge = 0;
  if (conciergeRes.status === 'fulfilled' && conciergeRes.value.data) {
    const c = conciergeRes.value.data;
    if (Array.isArray(c)) {
      pendingConcierge = c.length;
    } else if (c?.requests) {
      pendingConcierge = c.requests.length;
    }
  }

  // Listing impressions from get_listing_event_stats
  let impressionsLast30d = 0;
  let listingCount = 0;
  if (listingStatsRes.status === 'fulfilled' && listingStatsRes.value.data) {
    const ls = listingStatsRes.value.data as { ok?: boolean; listings?: any[] };
    const listings = ls?.listings ?? (Array.isArray(listingStatsRes.value.data) ? listingStatsRes.value.data : []);
    listingCount = listings.length;
    impressionsLast30d = listings.reduce((sum: number, l: any) => sum + (l.impressions || 0), 0);
  }

  // Every real ad and business-pin impression, team views left out. Falls
  // back to the business-pin total above if the summary is unavailable.
  let businessPinImpressions: number | null = null;
  let teamImpressions: number | null = null;
  if (impressionsRes.status === 'fulfilled' && !impressionsRes.value.error) {
    const s = impressionsRes.value.data as {
      ok?: boolean;
      impressions?: number;
      business_pin_impressions?: number;
      team_impressions?: number;
    } | null;
    if (s?.ok) {
      impressionsLast30d = s.impressions ?? 0;
      businessPinImpressions = s.business_pin_impressions ?? 0;
      teamImpressions = s.team_impressions ?? 0;
    }
  }

  // Active sponsors count from ad_campaigns direct query
  let activeSponsors = 0;
  if (sponsorsRes.status === 'fulfilled' && !sponsorsRes.value.error) {
    activeSponsors = sponsorsRes.value.count || 0;
  }

  // Live festivals count from ad_campaigns direct query
  let liveFestivals = 0;
  if (festivalsRes.status === 'fulfilled' && !festivalsRes.value.error) {
    liveFestivals = festivalsRes.value.count || 0;
  }

  return {
    onlineDrivers,
    totalDrivers,
    pendingConcierge,
    pendingPackages,
    packagesLast7d,
    impressionsLast30d,
    businessPinImpressions,
    teamImpressions,
    listingCount,
    activeSponsors,
    liveFestivals,
    rlsRecursionDetected,
  };
}

// ─── Audit Log ───────────────────────────────────────────────────────────────

export async function getAuditLog(params: {
  page?: number;
  limit?: number;
  action?: string;
  admin_id?: string;
} = {}) {
  const page = params.page || 1;
  const limit = params.limit || 25;

  // Routed through `list_audit_log_admin` (SECURITY DEFINER)
  // because the per-row admin identity needs a join against
  // `auth.users.email`, which isn't directly queryable from
  // the `authenticated` role. The RPC returns the same shape
  // the page expects, plus three new identity fields:
  // `admin_full_name`, `admin_avatar_url`, `admin_email`.
  const { data, error } = await supabase.rpc('list_audit_log_admin', {
    p_page: page,
    p_limit: limit,
    p_action: params.action ?? null,
    p_admin: params.admin_id ?? null,
  });
  if (error) throw error;

  const res = (data ?? {}) as {
    ok?: boolean;
    error?: string;
    entries?: unknown[];
    total?: number;
  };
  if (!res.ok) {
    throw new Error(res.error || 'Failed to load audit log');
  }

  return {
    data: (res.entries ?? []) as Record<string, unknown>[],
    total: res.total ?? 0,
    page,
    limit,
  };
}

// ─── Ad Campaigns (Sponsors + Festivals) ─────────────────────────────────────

/**
 * Staff never read ad_campaigns rows from the table: the server refuses it
 * because it cannot see which columns were asked for. They read through
 * admin_staff_campaigns, which drops billing fields.
 */
let campaignsViaStaffReader = false;

/** Called by AdminAuthContext: managers read the table, staff use the reader. */
export function setCampaignBillingAccess(seesMoney: boolean) {
  campaignsViaStaffReader = !seesMoney;
}

/** Campaigns without billing fields, for staff with the sponsors area. */
export async function staffCampaigns(params: {
  search?: string;
  placement?: string;
  listingId?: string;
  brandName?: string;
  ids?: string[];
  live?: boolean;
  festival?: boolean;
  limit?: number;
  offset?: number;
}): Promise<{ rows: any[]; total: number }> {
  const { data, error } = await supabase.rpc('admin_staff_campaigns', {
    p_search: params.search ?? null,
    p_placement: params.placement ?? null,
    p_listing_id: params.listingId ?? null,
    p_brand_name: params.brandName ?? null,
    p_ids: params.ids ?? null,
    p_live: params.live ?? false,
    p_festival: params.festival ?? null,
    p_limit: params.limit ?? 25,
    p_offset: params.offset ?? 0,
  });
  if (error) throw error;
  const res = data as { ok?: boolean; error?: string; rows?: any[]; total?: number };
  if (!res?.ok) throw new Error(res?.error || 'Could not load campaigns');
  return { rows: res.rows ?? [], total: res.total ?? 0 };
}

/** True when campaign reads must go through staffCampaigns. */
export function readsCampaignsAsStaff() {
  return campaignsViaStaffReader;
}

export async function getCampaigns(params: {
  page?: number;
  limit?: number;
  placement?: string;
  search?: string;
} = {}) {
  const page = params.page || 1;
  const limit = params.limit || 25;
  const offset = (page - 1) * limit;

  if (campaignsViaStaffReader) {
    const res = await staffCampaigns({ search: params.search, placement: params.placement, limit, offset });
    return { data: res.rows, total: res.total, page, limit };
  }

  let query = supabase
    .from('ad_campaigns')
    .select('*', { count: 'exact' })
    .order('created_at', { ascending: false })
    .range(offset, offset + limit - 1);

  if (params.placement) query = query.eq('placement', params.placement);
  if (params.search) query = query.ilike('brand_name', `%${params.search}%`);

  const { data, count, error } = await query;
  if (error) throw error;

  return { data: data || [], total: count || 0, page, limit };
}

export async function createCampaign(campaign: Record<string, unknown>) {
  const { data, error } = await supabase
    .from('ad_campaigns')
    .insert(campaign)
    .select()
    .single();
  if (error) throw error;

  await logAction('create_campaign', 'ad_campaigns', data.id, campaign);
  return data;
}

export async function updateCampaign(id: string, updates: Record<string, unknown>) {
  if (campaignsViaStaffReader) {
    const { error: updErr } = await supabase.from('ad_campaigns').update(updates).eq('id', id);
    if (updErr) throw updErr;
    await logAction('update_campaign', 'ad_campaigns', id, updates);
    const res = await staffCampaigns({ ids: [id], limit: 1 });
    return res.rows[0] ?? null;
  }

  const { data, error } = await supabase
    .from('ad_campaigns')
    .update(updates)
    .eq('id', id)
    .select()
    .single();
  if (error) throw error;

  await logAction('update_campaign', 'ad_campaigns', id, updates);
  return data;
}

export async function deleteCampaign(id: string) {
  const { error } = await supabase
    .from('ad_campaigns')
    .delete()
    .eq('id', id);
  if (error) throw error;

  await logAction('delete_campaign', 'ad_campaigns', id, {});
}

// ─── Local Business Listings ─────────────────────────────────────────────────

export async function getListings(params: {
  page?: number;
  limit?: number;
  search?: string;
} = {}) {
  const page = params.page || 1;
  const limit = params.limit || 25;
  const offset = (page - 1) * limit;

  let query = supabase
    .from('local_business_listings')
    .select('*', { count: 'exact' })
    .order('name', { ascending: true })
    .range(offset, offset + limit - 1);

  if (params.search) query = query.ilike('name', `%${params.search}%`);

  const { data, count, error } = await query;
  if (error) throw error;

  return { data: data || [], total: count || 0, page, limit };
}

export async function createListing(listing: Record<string, unknown>) {
  const { data, error } = await supabase
    .from('local_business_listings')
    .insert(listing)
    .select()
    .single();
  if (error) throw error;

  await logAction('create_listing', 'local_business_listings', data.id, listing);
  return data;
}

export async function updateListing(id: string, updates: Record<string, unknown>) {
  const { data, error } = await supabase
    .from('local_business_listings')
    .update(updates)
    .eq('id', id)
    .select()
    .single();
  if (error) throw error;

  await logAction('update_listing', 'local_business_listings', id, updates);
  return data;
}

export async function deleteListing(id: string) {
  const { error } = await supabase
    .from('local_business_listings')
    .delete()
    .eq('id', id);
  if (error) throw error;

  await logAction('delete_listing', 'local_business_listings', id, {});
}

// ─── B2G Dashboard ───────────────────────────────────────────────────────────

export async function getB2GStats(days: number = 7) {
  const [corridorStats, listingStats] = await Promise.allSettled([
    supabase.rpc('get_b2g_corridor_stats', { p_days: days }),
    supabase.rpc('get_listing_event_stats', { p_days: days })
  ]);

  // get_b2g_corridor_stats has a SQL bug (r.created_at should be r.accepted_at)
  // Handle gracefully
  let corridorData = null;
  let corridorError: string | null = null;
  if (corridorStats.status === 'fulfilled') {
    if (corridorStats.value.error) {
      corridorError = corridorStats.value.error.message;
    } else {
      corridorData = corridorStats.value.data;
    }
  }

  let listingData = null;
  if (listingStats.status === 'fulfilled' && !listingStats.value.error) {
    const ls = listingStats.value.data as { ok?: boolean; listings?: any[] };
    listingData = ls?.listings ?? (Array.isArray(listingStats.value.data) ? listingStats.value.data : []);
  }

  return {
    corridorStats: corridorData,
    corridorError,
    listingStats: listingData
  };
}

export interface TransitReport {
  ok: boolean;
  days: number;
  include_team: boolean;
  /** Journeys seen fewer times than this are not listed individually. */
  min_cell: number;
  timezone: string;
  generated_at: string;
  team_trips_excluded: number;
  summary: {
    requested: number;
    completed: number;
    unmet: number;
    cancelled_after_match: number;
    fulfilment_rate: number | null;
    median_wait_min: number | null;
    median_pickup_min: number | null;
    median_trip_min: number | null;
    passengers_carried: number;
    fare_value_xcd: number;
    active_buses: number;
    unique_riders: number | null;
  };
  daily: { date: string; requested: number; completed: number; unmet: number }[];
  corridors: {
    corridor_id: string;
    name: string;
    requested: number;
    completed: number;
    unmet: number;
    median_wait_min: number | null;
    passengers: number;
    fare_value_xcd: number;
  }[];
  heatmap: { dow: number; hour: number; requests: number }[];
  journeys: {
    top: { origin: string; destination: string; requested: number; completed: number }[];
    other_requested: number;
  };
  interest: { filter_id: string; count: number }[];
  community: {
    hazards: { type: string; count: number }[];
    utility_reports: { utility: string; kind: string; count: number }[];
    sos_events: number;
  };
}

/**
 * Aggregated public-transport report for government partners. Counts and
 * medians only; team test activity is left out unless `includeTeam` is set.
 */
export async function getB2GTransitReport(days: number, includeTeam: boolean): Promise<TransitReport> {
  const { data, error } = await supabase.rpc('get_b2g_transit_report', {
    p_days: days,
    p_include_team: includeTeam,
  });
  if (error) throw error;
  const report = data as TransitReport & { error?: string };
  if (!report?.ok) throw new Error(report?.error || 'Could not load the transit report');
  return report;
}

// ─── Hotel Concierge ─────────────────────────────────────────────────────────

export async function getConciergeRequests(status?: string) {
  const { data, error } = await supabase.rpc('list_concierge_requests_admin', {
    p_status: status || null
  });
  if (error) throw error;
  // The RPC uses verbose field names (guest_label, pickup_label, etc.).
  // Normalize here so callers use the simpler UI-facing names.
  const raw: any[] = Array.isArray(data) ? data : (data?.requests ?? []);
  return raw.map((r: any) => ({
    ...r,
    guest_name:          r.guest_label          ?? r.guest_name          ?? '–',
    pickup_location:     r.pickup_label          ?? r.pickup_location     ?? '–',
    dropoff_location:    r.dropoff_label         ?? r.dropoff_location    ?? '–',
    pickup_time:         r.scheduled_for         ?? r.pickup_time         ?? null,
    driver_id:           r.assigned_driver_id    ?? r.driver_id           ?? null,
    driver_name:         r.assigned_driver_name  ?? r.driver_name         ?? null,
    driver_phone:        r.assigned_driver_phone ?? r.driver_phone        ?? null,
    driver_license_plate: r.assigned_driver_license_plate ?? r.driver_license_plate ?? null,
  }));
}

export interface NewConciergeRequest {
  hotelName: string;
  pickupLabel: string;
  dropoffLabel: string;
  guestLabel?: string | null;
  guestPhone?: string | null;
  priority?: 'standard' | 'vip';
  scheduledFor?: string | null;
  notes?: string | null;
  /** Party size — decides which buses have the seats for the job. */
  guestCount?: number;
  /**
   * Map pins for each end. A label like "Hotel lobby" is meaningless to a
   * driver's navigation app, and the pickup pin is also what lets dispatch
   * rank drivers by how close they actually are.
   */
  pickupLat?: number | null;
  pickupLng?: number | null;
  dropoffLat?: number | null;
  dropoffLng?: number | null;
}

export async function createConciergeRequest(request: NewConciergeRequest) {
  const { data, error } = await supabase.rpc('create_concierge_request', {
    p_hotel_name: request.hotelName,
    p_pickup_label: request.pickupLabel,
    p_dropoff_label: request.dropoffLabel,
    p_guest_label: request.guestLabel || null,
    p_guest_phone: request.guestPhone || null,
    p_priority: request.priority ?? 'standard',
    p_scheduled_for: request.scheduledFor || null,
    p_notes: request.notes || null,
    p_guest_count: request.guestCount ?? 1,
    p_pickup_lat: request.pickupLat ?? null,
    p_pickup_lng: request.pickupLng ?? null,
    p_dropoff_lat: request.dropoffLat ?? null,
    p_dropoff_lng: request.dropoffLng ?? null,
  });
  if (error) throw error;
  const result = data as { ok?: boolean; error?: string; id?: string } | null;
  if (result?.ok === false) throw new Error(result.error || 'Failed to create request');

  await logAction('create_concierge', 'hotel_concierge_requests', result?.id ?? null, {
    hotel_name: request.hotelName,
    priority: request.priority ?? 'standard',
  });
  return result;
}

/**
 * Send a wave of offers to the best available drivers. First to accept wins.
 *
 * This is the path that should normally be used: unlike a direct assignment,
 * the job only counts as handled once a driver has actually said yes, and if
 * nobody answers the operator is pushed to place it by phone.
 */
export async function dispatchConciergeRequest(requestId: string, maxOffers = 3) {
  const { data, error } = await supabase.rpc('dispatch_concierge_request', {
    p_request_id: requestId,
    p_max_offers: maxOffers,
  });
  if (error) throw error;
  const result = data as
    | { ok?: boolean; error?: string; offers?: number; escalated?: boolean }
    | null;
  if (result?.ok === false) throw new Error(result.error || 'Failed to send offers');

  await logAction('dispatch_concierge', 'hotel_concierge_requests', requestId, {
    offers: result?.offers ?? 0,
  });
  return {
    offers: Number(result?.offers ?? 0),
    escalated: result?.escalated === true,
  };
}

/**
 * A hotel or fixed destination the operator has saved, so its name, exact spot
 * and pin can be reused instead of retyped on every phone call.
 */
export interface ConciergePlace {
  id: string;
  kind: 'hotel' | 'destination';
  name: string;
  spot_label: string | null;
  lat: number | null;
  lng: number | null;
  contact_phone: string | null;
  notes: string | null;
  default_priority: 'standard' | 'vip';
  is_active: boolean;
  /** Requests logged against this place — lets the picker float busy accounts. */
  use_count: number;
}

export async function getConciergePlaces(includeArchived = false) {
  const { data, error } = await supabase.rpc('list_concierge_places', {
    p_include_archived: includeArchived,
  });
  if (error) throw error;
  const result = data as { ok?: boolean; error?: string; places?: ConciergePlace[] } | null;
  if (result?.ok === false) throw new Error(result.error || 'Failed to load places');
  return result?.places ?? [];
}

export async function upsertConciergePlace(place: {
  id?: string | null;
  kind: 'hotel' | 'destination';
  name: string;
  spotLabel?: string | null;
  lat?: number | null;
  lng?: number | null;
  contactPhone?: string | null;
  notes?: string | null;
  defaultPriority?: 'standard' | 'vip';
}) {
  const { data, error } = await supabase.rpc('upsert_concierge_place', {
    p_kind: place.kind,
    p_name: place.name,
    p_id: place.id ?? null,
    p_spot_label: place.spotLabel ?? null,
    p_lat: place.lat ?? null,
    p_lng: place.lng ?? null,
    p_contact_phone: place.contactPhone ?? null,
    p_notes: place.notes ?? null,
    p_default_priority: place.defaultPriority ?? 'standard',
  });
  if (error) throw error;
  const result = data as { ok?: boolean; error?: string; id?: string } | null;
  if (result?.ok === false) throw new Error(result.error || 'Failed to save place');
  return result;
}

export async function setConciergePlaceActive(id: string, active: boolean) {
  const { data, error } = await supabase.rpc('set_concierge_place_active', {
    p_id: id,
    p_active: active,
  });
  if (error) throw error;
  const result = data as { ok?: boolean; error?: string } | null;
  if (result?.ok === false) throw new Error(result.error || 'Failed to update place');
  return result;
}

/**
 * Correct the pins (and optionally the labels) on a request that is already
 * open. Needed because the driver is usually the one who discovers the pin is
 * wrong — "that's the back gate, guests come out the other side" — and
 * rebuilding the request to fix it would discard its dispatch history.
 */
export async function setConciergeLocations(
  requestId: string,
  patch: {
    pickupLat?: number | null;
    pickupLng?: number | null;
    dropoffLat?: number | null;
    dropoffLng?: number | null;
    pickupLabel?: string | null;
    dropoffLabel?: string | null;
  },
) {
  const { data, error } = await supabase.rpc('set_concierge_locations', {
    p_request_id: requestId,
    p_pickup_lat: patch.pickupLat ?? null,
    p_pickup_lng: patch.pickupLng ?? null,
    p_dropoff_lat: patch.dropoffLat ?? null,
    p_dropoff_lng: patch.dropoffLng ?? null,
    p_pickup_label: patch.pickupLabel ?? null,
    p_dropoff_label: patch.dropoffLabel ?? null,
  });
  if (error) throw error;
  const result = data as { ok?: boolean; error?: string } | null;
  if (result?.ok === false) throw new Error(result.error || 'Failed to update locations');

  await logAction('repin_concierge', 'hotel_concierge_requests', requestId, patch);
  return result;
}

/**
 * Drivers for the concierge assign picker. Uses the concierge-specific RPC
 * rather than the general roster because it carries the DDA/NISE tourism
 * certification and GPS freshness — the two things that decide whether a
 * driver should be trusted with a paying hotel's guest.
 */
export async function getConciergeDrivers() {
  const { data, error } = await supabase.rpc('list_online_drivers_admin');
  if (error) throw error;
  const response = data as { ok?: boolean; error?: string; drivers?: any[] } | null;
  if (response?.ok === false) throw new Error(response.error || 'Failed to load drivers');
  return response?.drivers ?? [];
}

/**
 * Per-hotel service record for retainer reviews.
 */
export async function getConciergeReport(days = 30) {
  const { data, error } = await supabase.rpc('concierge_hotel_report', { p_days: days });
  if (error) throw error;
  const response = data as { ok?: boolean; error?: string } | null;
  if (response?.ok === false) throw new Error(response.error || 'Failed to load report');
  return data as {
    ok: true;
    days: number;
    hotels: HotelReportRow[];
    totals: HotelReportTotals;
  };
}

export interface HotelReportRow {
  hotel_name: string;
  requests: number;
  completed: number;
  cancelled: number;
  open: number;
  vip: number;
  median_minutes_to_assign: number | null;
  p90_minutes_to_assign: number | null;
  avg_minutes_to_complete: number | null;
  last_request_at: string | null;
}

export interface HotelReportTotals {
  requests: number;
  completed: number;
  cancelled: number;
  open: number;
  hotels: number;
  median_minutes_to_assign: number | null;
}

/**
 * Hand the job straight to one driver, skipping the accept step.
 *
 * Only correct when the operator has already spoken to that driver — the
 * driver never confirms in-app, so an unconfirmed direct assignment is exactly
 * the false confidence the offer flow exists to remove.
 */
export async function assignConciergeRequest(requestId: string, driverId: string) {
  const { data, error } = await supabase.rpc('assign_concierge_request', {
    p_request_id: requestId,
    p_driver_id: driverId
  });
  if (error) throw error;
  const result = data as any;
  if (result?.ok === false) throw new Error(result.error || 'Failed to assign driver');

  await logAction('assign_concierge', 'hotel_concierge_requests', requestId, { driver_id: driverId });
  return data;
}

export async function cancelConciergeRequest(requestId: string) {
  const { data, error } = await supabase.rpc('cancel_concierge_request', {
    p_request_id: requestId
  });
  if (error) throw error;
  const result = data as any;
  if (result?.ok === false) throw new Error(result.error || 'Failed to cancel request');

  await logAction('cancel_concierge', 'hotel_concierge_requests', requestId, {});
  return data;
}

export async function completeConciergeRequest(requestId: string) {
  // complete_concierge_request RPC is driver-only (checks assigned_driver_id = auth.uid()).
  // Admins complete requests via a direct status update instead.
  const { error } = await supabase
    .from('hotel_concierge_requests')
    .update({ status: 'completed' })
    .eq('id', requestId);
  if (error) throw error;

  await logAction('complete_concierge', 'hotel_concierge_requests', requestId, {});
}

// ─── Online Drivers ──────────────────────────────────────────────────────────

export async function getOnlineDrivers() {
  // Use list_drivers_admin and filter for online
  const { data, error } = await supabase.rpc('list_drivers_admin');
  if (error) throw error;
  const response = data as { ok?: boolean; drivers?: any[] };
  const drivers = response?.drivers ?? (Array.isArray(data) ? data : []);
  return drivers.filter((d: any) => d.is_online);
}

// ─── Drivers Management ──────────────────────────────────────────────────────

export async function getDrivers() {
  const { data, error } = await supabase.rpc('list_drivers_admin');
  if (error) throw error;
  const response = data as { ok?: boolean; drivers?: any[] };
  return response?.drivers ?? (Array.isArray(data) ? data : []);
}

export async function suspendDriver(userId: string, suspended: boolean, reason?: string) {
  const { error } = await supabase.rpc('set_user_suspension', {
    p_user_id: userId,
    p_suspended: suspended,
    p_reason: reason || null
  });
  if (error) throw error;

  await logAction(
    suspended ? 'suspend_driver' : 'unsuspend_driver',
    'profiles',
    userId,
    { reason }
  );
}

// ─── Package Ledger ──────────────────────────────────────────────────────────

export async function getPackages(params: {
  days?: number;
  status?: string;
  driver_id?: string;
} = {}) {
  const { data, error } = await supabase.rpc('list_packages_admin', {
    p_days: params.days || 30,
    p_status: params.status || null,
    p_driver_id: params.driver_id || null
  });
  if (error) throw error;
  return data;
}

// ─── Platform Admins (Settings) ──────────────────────────────────────────────

export async function getPlatformAdmins() {
  const { data, error } = await supabase.rpc('list_platform_admins');
  if (error) throw error;
  return data || [];
}

export async function addPlatformAdmin(userId: string) {
  const { data, error } = await supabase.rpc('add_platform_admin', {
    p_user_id: userId
  });
  if (error) throw error;
  return data;
}

export async function removePlatformAdmin(userId: string) {
  const { data, error } = await supabase.rpc('remove_platform_admin', {
    p_user_id: userId
  });
  if (error) throw error;
  return data;
}

// ─── Admin Team ──────────────────────────────────────────────────────────────

/**
 * A team member. Staff viewing the list without the audit area only receive
 * user_id, display_name, role and status for other people.
 */
export interface AdminTeamMember {
  user_id: string;
  email?: string;
  display_name: string;
  role: string;
  areas?: string[];
  status: 'ready' | 'invited' | 'active' | 'disabled';
  /** Has a verified authenticator app. Only sent with the full member view. */
  two_step?: boolean;
  created_at?: string;
  created_by_name?: string | null;
  invited_at?: string | null;
  activated_at?: string | null;
  disabled_at?: string | null;
  last_sign_in_at?: string | null;
  actions_30d?: number;
  last_action_at?: string | null;
}

/** Everyone on the admin team, with sign-in and activity summaries. */
export async function getAdminTeam(): Promise<{
  viewerIsOwner: boolean;
  viewerRole: string | null;
  members: AdminTeamMember[];
}> {
  const { data, error } = await supabase.rpc('list_admin_team');
  if (error) throw error;
  const res = data as {
    ok?: boolean;
    error?: string;
    viewer_is_owner?: boolean;
    viewer_role?: string;
    members?: AdminTeamMember[];
  };
  if (!res?.ok) throw new Error(res?.error || 'Could not load the team');
  return { viewerIsOwner: !!res.viewer_is_owner, viewerRole: res.viewer_role ?? null, members: res.members ?? [] };
}

const TEAM_ERRORS: Record<string, string> = {
  owner_only: 'Only the owner can add or change directors.',
  not_allowed: 'Only the owner and directors can change the team.',
  not_found: 'That account no longer exists.',
  invalid_area: 'One of the chosen areas is not recognised.',
  no_areas: 'Choose at least one area for this person.',
  owner_protected: 'The owner account cannot be changed here.',
  email_in_use: 'That email already has an H-Plate account. Use a different one, for example with +hplate before the @.',
  invalid_email: 'Enter a valid email address.',
  invalid_name: 'Enter their name.',
  invalid_role: 'That role is not available yet.',
  disabled: 'Switch the account back on first.',
  only_unused_accounts: 'Only accounts that were never used can be deleted. Switch it off instead.',
  no_two_step: 'This person has not set up two-step sign-in yet, so there is nothing to reset.',
  two_step_required: 'Enter a fresh code from your authenticator app, then try again.',
};

/**
 * Team change through the admin-team Edge Function. The owner manages
 * directors and staff; directors manage staff. `invite`
 * returns the one-time temporary password, which is never stored.
 */
export async function adminTeamAction(
  body:
    | { action: 'create'; email: string; display_name: string; role: string; areas?: string[] }
    | { action: 'update'; user_id: string; role?: string; areas?: string[]; display_name?: string }
    | { action: 'invite' | 'reset_two_step' | 'disable' | 'enable' | 'delete'; user_id: string },
): Promise<{ ok: true; temporary_password?: string; email?: string }> {
  const { data, error } = await supabase.functions.invoke('admin-team', { body });
  let payload = data as { ok?: boolean; error?: string; temporary_password?: string; email?: string } | null;
  if (error) {
    const response = (error as { context?: Response }).context;
    payload = response ? await response.json().catch(() => null) : null;
  }
  if (!payload?.ok) {
    const code = payload?.error ?? error?.message ?? 'failed';
    throw new Error(TEAM_ERRORS[code] ?? code);
  }
  return payload as { ok: true; temporary_password?: string; email?: string };
}

// ─── Requests to the directors ───────────────────────────────────────────────

export type AdminRequestKind = 'approval' | 'decision' | 'question' | 'access' | 'other';
export type AdminRequestStatus = 'open' | 'approved' | 'declined' | 'answered' | 'withdrawn';

export interface AdminRequestPerson {
  user_id: string;
  display_name: string;
  role: string;
}

export interface AdminRequest {
  id: string;
  kind: AdminRequestKind;
  subject: string;
  body: string;
  page_path: string | null;
  status: AdminRequestStatus;
  created_at: string;
  last_activity_at: string;
  resolved_at: string | null;
  created_by: AdminRequestPerson;
  assigned_to: AdminRequestPerson | null;
  resolved_by: AdminRequestPerson | null;
  is_mine: boolean;
  unread: boolean;
  message_count: number;
}

export interface AdminRequestMessage {
  id: string;
  body: string;
  status_change: AdminRequestStatus | null;
  created_at: string;
  author: AdminRequestPerson | null;
  is_mine: boolean;
}

const REQUEST_ERRORS: Record<string, string> = {
  forbidden: 'Your account cannot use requests.',
  not_found: 'That request is no longer available.',
  invalid_kind: 'Choose what kind of request this is.',
  invalid_subject: 'Give the request a short subject (3 to 120 characters).',
  invalid_body: 'Write a message (up to 4000 characters).',
  invalid_assignee: 'That person cannot receive requests. Pick a director or leave it for any director.',
  too_many: 'You have sent a lot of requests today. Talk to a director directly.',
  managers_only: 'Only the owner and directors can decide on a request.',
  cannot_withdraw: 'Only an open request you sent can be withdrawn.',
  invalid_status: 'That is not a valid status.',
};

async function requestRpc<T>(fn: string, args: Record<string, unknown> = {}): Promise<T> {
  const { data, error } = await supabase.rpc(fn, args);
  if (error) throw error;
  const res = data as { ok?: boolean; error?: string } & T;
  if (!res?.ok) {
    const code = res?.error ?? 'failed';
    throw new Error(REQUEST_ERRORS[code] ?? code);
  }
  return res;
}

/** Requests the signed-in person can see: their own, or everyone's for managers. */
export async function listAdminRequests(view: 'open' | 'closed'): Promise<{ viewerIsManager: boolean; rows: AdminRequest[] }> {
  const res = await requestRpc<{ viewer_is_manager: boolean; rows: AdminRequest[] }>('admin_request_list', { p_view: view });
  return { viewerIsManager: !!res.viewer_is_manager, rows: res.rows ?? [] };
}

/** Sidebar counts: open requests, and how many need the viewer's attention. */
export async function getAdminRequestCounts(): Promise<{ open: number; needsAttention: number }> {
  const res = await requestRpc<{ open: number; needs_attention: number }>('admin_request_counts');
  return { open: res.open ?? 0, needsAttention: res.needs_attention ?? 0 };
}

/** One request with its full thread. Opening it marks it read. */
export async function getAdminRequest(id: string): Promise<{ viewerIsManager: boolean; request: AdminRequest; messages: AdminRequestMessage[] }> {
  const res = await requestRpc<{ viewer_is_manager: boolean; request: AdminRequest; messages: AdminRequestMessage[] }>('admin_request_get', { p_id: id });
  return { viewerIsManager: !!res.viewer_is_manager, request: res.request, messages: res.messages ?? [] };
}

/** Raise a new request for the directors, optionally addressed to one of them. */
export async function createAdminRequest(input: {
  kind: AdminRequestKind;
  subject: string;
  body: string;
  assignedTo?: string | null;
  pagePath?: string | null;
}): Promise<string> {
  const res = await requestRpc<{ id: string }>('admin_request_create', {
    p_kind: input.kind,
    p_subject: input.subject,
    p_body: input.body,
    p_assigned_to: input.assignedTo ?? null,
    p_page_path: input.pagePath ?? null,
  });
  emailRequestEvent(res.id);
  return res.id;
}

/** Add a message to a request thread. */
export async function replyToAdminRequest(id: string, body: string): Promise<void> {
  await requestRpc('admin_request_reply', { p_id: id, p_body: body });
  emailRequestEvent(id);
}

/** Managers approve, decline, answer or reopen; the sender can withdraw. */
export async function setAdminRequestStatus(id: string, status: AdminRequestStatus, note?: string): Promise<void> {
  await requestRpc('admin_request_set_status', { p_id: id, p_status: status, p_note: note ?? null });
  emailRequestEvent(id);
}

/** Emails the other side about the request's latest event. Never blocks or throws. */
function emailRequestEvent(requestId: string): void {
  supabase.functions.invoke('admin-request-email', { body: { request_id: requestId } }).catch(() => {});
}

// ─── Storage Upload ──────────────────────────────────────────────────────────

export async function uploadFile(file: File, folder: string = 'admin-uploads') {
  // Downscale images on ingest (no-op for non-image files); keeps stored
  // creatives phone-sized so users on prepaid data don't download megapixels.
  const optimized = await optimizeImageForUpload(file, PHOTO_PRESET);
  const filePath = `${folder}/${Date.now()}-${optimized.name}`;

  const { data, error } = await supabase.storage
    .from('monetization-assets')
    .upload(filePath, optimized, {
      contentType: optimized.type,
      upsert: true
    });

  if (error) throw error;

  const { data: urlData } = supabase.storage
    .from('monetization-assets')
    .getPublicUrl(data.path);

  return { url: urlData.publicUrl, path: data.path };
}

// ─── Realtime Subscription ───────────────────────────────────────────────────

export function subscribeToConciergeRequests(
  callback: (payload: any) => void
) {
  const channel = supabase
    .channel('admin-concierge')
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'hotel_concierge_requests'
      },
      callback
    )
    .subscribe();

  return () => {
    supabase.removeChannel(channel);
  };
}

// ─── Convenience export ──────────────────────────────────────────────────────

export const adminApi = {
  getOverview,
  getAuditLog,
  getCampaigns,
  createCampaign,
  updateCampaign,
  deleteCampaign,
  getListings,
  createListing,
  updateListing,
  deleteListing,
  getB2GStats,
  getB2GTransitReport,
  getAdminTeam,
  adminTeamAction,
  getConciergeRequests,
  createConciergeRequest,
  dispatchConciergeRequest,
  setConciergeLocations,
  getConciergePlaces,
  upsertConciergePlace,
  setConciergePlaceActive,
  assignConciergeRequest,
  cancelConciergeRequest,
  completeConciergeRequest,
  getConciergeDrivers,
  getConciergeReport,
  getOnlineDrivers,
  getDrivers,
  suspendDriver,
  getPackages,
  getPlatformAdmins,
  addPlatformAdmin,
  removePlatformAdmin,
  uploadFile,
  subscribeToConciergeRequests,
  logAction
};
